import classnames from 'classnames';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useSidebarOpenState, sidebarConfig } from '@backstage/core-components';

import {
  Collapse,
  makeStyles,
  Theme,
  Typography,
  Box,
  ButtonBase,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const ICON_SIZE = 20;
const ICON_SLOT_WIDTH = 40;
const HEADER_HEIGHT = 48;
const CHILD_HEIGHT = 36;
const LEFT_PADDING = 16;

const GUIDE_LINE_X = LEFT_PADDING + ICON_SLOT_WIDTH / 2;
const GUIDE_LINE_BORDER = 2;
const CHILD_LEFT_PADDING = 1;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
  },
  rootClosed: {
    width: sidebarConfig.drawerWidthClosed,
    overflow: 'hidden',
  },
  summaryButton: {
    width: '100%',
    height: HEADER_HEIGHT,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: LEFT_PADDING,
    color: theme.palette.navigation?.color ?? theme.palette.text.secondary,
    opacity: 0.5,
    transition: 'background-color 0.15s ease, opacity 0.15s ease',
    '&:hover': {
      opacity: 1,
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
    },
  },
  summaryButtonClosed: {
    width: sidebarConfig.drawerWidthClosed,
    justifyContent: 'center',
    paddingLeft: 0,
    paddingRight: 0,
  },
  summaryButtonActive: {
    backgroundColor: '#df1b12',
    color: theme.palette.common.white,
    opacity: 1,
    '&:hover': {
      backgroundColor: '#df1b12 !important',
      color: `${theme.palette.common.white} !important`,
      opacity: 1,
    },
  },
  summaryButtonHasActiveChild: {
    backgroundColor: '#df1b12',
    color: theme.palette.common.white,
    opacity: 1,
    '&:hover': {
      backgroundColor: '#df1b12 !important',
      color: `${theme.palette.common.white} !important`,
      opacity: 1,
    },
  },
  iconSlot: {
    width: ICON_SLOT_WIDTH,
    minWidth: ICON_SLOT_WIDTH,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& > svg, & > img': {
      width: ICON_SIZE,
      height: ICON_SIZE,
      fontSize: ICON_SIZE,
      color: 'inherit',
    },
  },
  label: {
    flex: 1,
    fontFamily: '"Proxima Nova", ui-sans-serif, system-ui, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '16px',
    color: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
  },
  expandIcon: {
    color: 'inherit',
    fontSize: '16px',
    flexShrink: 0,
    opacity: 0.7,
    transition: 'transform 0.2s ease',
    marginRight: 8,
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },

  childrenWrapper: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 2,
    paddingBottom: 6,
    marginLeft: GUIDE_LINE_X,
    borderLeft: `${GUIDE_LINE_BORDER}px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.15)'
        : 'rgba(0,0,0,0.12)'
    }`,
    '& > a[href]:not([aria-current="page"]), & > a[tabindex]:not([aria-current="page"]), & > button[type]:not([aria-current="page"]), & > button:not([type]):not([aria-current="page"])':
      {
        height: `${CHILD_HEIGHT}px !important`,
        minHeight: `${CHILD_HEIGHT}px !important`,
        maxHeight: `${CHILD_HEIGHT}px !important`,
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        paddingLeft: `${CHILD_LEFT_PADDING}px !important`,
        boxSizing: 'border-box',
        borderLeft: 'none !important',
        marginLeft: '0 !important',
        width: '100% !important',
        display: 'flex !important',
        alignItems: 'center !important',
        backgroundColor: 'transparent !important',
        color: 'inherit !important',
        opacity: 0.5,
        transition: 'opacity 0.15s ease',
      },

    '& > a[aria-current="page"], & > button[aria-current="page"]': {
      height: `${CHILD_HEIGHT}px !important`,
      minHeight: `${CHILD_HEIGHT}px !important`,
      maxHeight: `${CHILD_HEIGHT}px !important`,
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      paddingLeft: `${CHILD_LEFT_PADDING}px !important`,
      boxSizing: 'border-box',
      width: `calc(100% + ${GUIDE_LINE_BORDER}px) !important`,
      display: 'flex !important',
      alignItems: 'center !important',
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
      borderLeft: `3px solid ${theme.palette.primary.main} !important`,
      marginLeft: `-${GUIDE_LINE_BORDER}px !important`,
      opacity: 1,
      transition: 'opacity 0.15s ease',
    },

    '& > a[href]:hover, & > a[tabindex]:hover, & > button:hover': {
      opacity: 1,
    },
    '& > a svg, & > button svg': {
      width: `${ICON_SIZE}px !important`,
      height: `${ICON_SIZE}px !important`,
      fontSize: `${ICON_SIZE}px !important`,
      flexShrink: 0,
    },
    '& > a img, & > button img': {
      width: `${ICON_SIZE}px !important`,
      height: `${ICON_SIZE}px !important`,
      objectFit: 'contain',
      flexShrink: 0,
    },

    '& > a span, & > button span': {
      fontFamily:
        '"Proxima Nova", ui-sans-serif, system-ui, sans-serif !important',
      fontSize: '14px !important',
      fontWeight: '500 !important',
      lineHeight: '16px !important',
    },
  },
}));

type SidebarAccordionGroupProps = {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  to?: string;
};

export const SidebarAccordionGroup = ({
  icon,
  label,
  children,
  to,
}: SidebarAccordionGroupProps) => {
  const classes = useStyles();
  const location = useLocation();
  const [expanded, setExpanded] = useState(true);
  const [isSummaryActive, setIsSummaryActive] = useState(false);
  const [hasActiveChild, setHasActiveChild] = useState(false);
  const { isOpen, setOpen } = useSidebarOpenState();

  useEffect(() => {
    let isActiveChild = false;
    React.Children.forEach(children, child => {
      if (React.isValidElement(child) && child.props.to) {
        const childPath = String(child.props.to)
          .split('?')[0]
          .replace(/^\//, '');
        const currentPath = location.pathname.replace(/^\//, '');
        if (
          currentPath === childPath ||
          currentPath.startsWith(`${childPath}/`)
        ) {
          isActiveChild = true;
        }
      }
    });

    const isGroupActive = to
      ? (() => {
          const groupPath = to.split('?')[0].replace(/^\//, '');
          const currentPath = location.pathname.replace(/^\//, '');
          return (
            currentPath === groupPath || currentPath.startsWith(`${groupPath}/`)
          );
        })()
      : false;

    setIsSummaryActive(isGroupActive);
    setHasActiveChild(isActiveChild);

    if (isActiveChild || isGroupActive) {
      setExpanded(true);
    }
  }, [location.pathname, children, to]);

  useEffect(() => {
    if (!isOpen) {
      setExpanded(false);
    }
  }, [isOpen]);

  const handleClick = () => {
    if (isOpen) {
      setExpanded(prev => !prev);
    } else {
      setOpen(true);
      setExpanded(true);
    }
  };

  return (
    <Box className={classnames(classes.root, !isOpen && classes.rootClosed)}>
      <ButtonBase
        onClick={handleClick}
        disableRipple
        className={classnames(
          classes.summaryButton,
          !isOpen && classes.summaryButtonClosed,
          isSummaryActive && classes.summaryButtonActive,
          hasActiveChild && classes.summaryButtonHasActiveChild,
        )}
      >
        {isOpen ? (
          <>
            <Box className={classes.iconSlot}>{icon}</Box>
            <Typography className={classes.label}>{label}</Typography>
            <ExpandMoreIcon
              className={classnames(
                classes.expandIcon,
                expanded && classes.expandIconOpen,
              )}
            />
          </>
        ) : (
          icon
        )}
      </ButtonBase>

      <Collapse in={expanded && isOpen} timeout="auto" unmountOnExit>
        <Box className={classes.childrenWrapper}>{children}</Box>
      </Collapse>
    </Box>
  );
};
